import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import Breadcrumb from '../components/common/Breadcrumb';
import EntityTable from '../components/common/EntityTable';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import HighlightedText from '../components/common/HighlightedText';
import { renderActionButton } from '../components/common/EntityTable';
import { renderEquipoCell } from '../utils/renderHelpers';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';
import ROUTES from '../constants/routes';
import { validateDate } from '../utils/sanitization';
import { getDateKey } from '../utils/dateHelpers';
import { useExportExcel } from '../hooks/useExportExcel';

/**
 * Pagina de gestion de programas de mantenimiento recurrente
 *
 * Permite crear, editar, eliminar y filtrar programas que generan
 * mantenimientos automaticamente segun frecuencia configurada.
 * Al crear un programa se genera automaticamente la primera mantencion.
 *
 * @returns {JSX.Element}
 */
const PRIORIDAD_OPTIONS = [
  { value: 'baja', label: 'Baja' },
  { value: 'media', label: 'Media' },
  { value: 'alta', label: 'Alta' },
  { value: 'critica', label: 'Critica' }
];

const FRECUENCIA_TIPO_OPTIONS = [
  { value: 'diaria', label: 'Diaria' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'mensual', label: 'Mensual' },
  { value: 'anual', label: 'Anual' }
];

const FRECUENCIA_LABELS = { diaria: 'dia(s)', semanal: 'semana(s)', mensual: 'mes(es)', anual: 'ano(s)' };

/**
 * Formatea tipo de frecuencia a texto legible
 * @param {Object} row - Fila de datos del programa
 * @returns {string}
 */
const formatFrecuencia = (row) => {
  return `Cada ${row.frecuencia_valor} ${FRECUENCIA_LABELS[row.frecuencia_tipo] || row.frecuencia_tipo}`;
};

function Programas() {
  const { hasPermission } = useAuth();

  const {
    data: programas,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem: editingPrograma,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm,
    refetch
  } = usePaginatedEntity({
    entityName: 'programa',
    entityNamePlural: 'programas',
    fetchFn: apiService.getProgramas,
    createFn: apiService.createPrograma,
    updateFn: apiService.updatePrograma,
    deleteFn: apiService.deletePrograma,
    defaultFilters: { search: '', equipo_id: '', frecuencia_tipo: '' },
    filterKeys: ['search', 'equipo_id', 'frecuencia_tipo'],
    messages: {
      createSuccess: 'Programa creado',
      updateSuccess: 'Programa actualizado',
      deleteSuccess: 'Programa eliminado exitosamente'
    }
  });

  const { showToast } = useToast();

  // Estado para pause/resume
  const [togglingId, setTogglingId] = useState(null);
  const [reactivateModal, setReactivateModal] = useState({ open: false, programa: null });
  const [reactivating, setReactivating] = useState(false);

  // Carga de catalogos
  const [equipos, setEquipos] = useState([]);
  const [plantillas, setPlantillas] = useState([]);

  /**
   * Carga catalogos de equipos, tipos y plantillas
   * @returns {Promise<void>}
   */
  const fetchCatalogos = useCallback(async () => {
    try {
      const [equiposRes, plantillasRes] = await Promise.all([
        apiService.getEquipos({ limit: 1000 }),
        apiService.getPlantillasMantenimientoLista()
      ]);
      if (equiposRes.data) setEquipos(equiposRes.data);
      if (plantillasRes.success) setPlantillas(plantillasRes.data || []);
    } catch (error) {
      console.error('Error fetching catalogos:', error);
    }
  }, []);

  useEffect(() => {
    fetchCatalogos();
  }, [fetchCatalogos]);

  /**
   * Pausa un programa (activo → inactivo)
   * @param {Object} row - Programa a pausar
   */
  const handlePause = async (row) => {
    setTogglingId(row.id);
    try {
      const response = await apiService.updatePrograma(row.id, { activo: 0 });
      if (response.success) {
        showToast('Programa pausado', 'success');
        refetch();
      } else {
        showToast(response.message || 'Error al pausar programa', 'error');
      }
    } catch (error) {
      showToast('Error al pausar programa', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  /**
   * Reanuda un programa pausado. Si hay mantención activa, toggle directo.
   * Si no hay activa, abre modal para pedir fecha.
   * @param {Object} row - Programa a reanudar
   */
  const handleResume = async (row) => {
    setTogglingId(row.id);
    if (row.has_active_maintenance) {
      try {
        const response = await apiService.updatePrograma(row.id, { activo: 1 });
        if (response.success) {
          showToast('Programa reanudado', 'success');
          refetch();
        } else {
          showToast(response.message || 'Error al reanudar programa', 'error');
        }
      } catch (error) {
        showToast('Error al reanudar programa', 'error');
      } finally {
        setTogglingId(null);
      }
    } else {
      setReactivateModal({ open: true, programa: row });
      // togglingId se limpia en handleReactivate (éxito) o al cerrar el modal
    }
  };

  /**
   * Reactiva programa desde modal: genera mantención + activa atómicamente
   * @param {Object} formData - Datos del formulario con proxima_ejecucion
   */
  const handleReactivate = async (formData) => {
    setReactivating(true);
    try {
      const response = await apiService.reactivarPrograma(
        reactivateModal.programa.id,
        { proxima_ejecucion: formData.proxima_ejecucion }
      );
      if (response.success) {
        showToast('Programa reactivado', 'success');
        setReactivateModal({ open: false, programa: null });
        setTogglingId(null);
        refetch();
      } else {
        showToast(response.message || 'Error al reactivar programa', 'error');
      }
    } catch (error) {
      showToast('Error al reactivar programa', 'error');
    } finally {
      setReactivating(false);
    }
  };

  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (p) => ({
      'Nombre': p.nombre,
      'Equipo': p.equipo ? `${p.equipo.codigo} - ${p.equipo.marca} ${p.equipo.modelo}` : '',
      'Plantilla': p.plantilla?.nombre || '',
      'Frecuencia': formatFrecuencia(p),
      'Próxima Ejecución': p.proxima_ejecucion ? new Date(p.proxima_ejecucion).toLocaleDateString('es-CL') : '',
      'Generados': p.total_generados || 0,
      'Activo': p.activo === 1 ? 'Sí' : 'Pausado'
    }),
    sheetName: 'Programas',
    columnWidths: [{ wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 8 }],
    entityLabel: 'programas'
  });

  const columns = useMemo(() => [
    {
      key: 'nombre',
      label: 'Programa',
      sortable: true,
      render: (row, searchTerm) => (
        <>
          <HighlightedText text={row.nombre || ''} searchTerm={searchTerm} />
          {row.activo === 0 && <span className="badge badge-warning margin-left-xs">Pausado</span>}
        </>
      )
    },
    {
      key: 'equipo',
      label: 'Equipo',
      render: (row, searchTerm) => renderEquipoCell(row.equipo, searchTerm)
    },
    {
      key: 'plantilla',
      label: 'Plantilla',
      render: (row) => row.plantilla
        ? <span>{row.plantilla.nombre}</span>
        : <span className="text-muted">—</span>
    },
    {
      key: 'frecuencia',
      label: 'Frecuencia',
      render: (row) => formatFrecuencia(row)
    },
    {
      key: 'proxima_ejecucion',
      label: 'Proxima',
      sortable: true,
      render: (row) => {
        const fecha = new Date(row.proxima_ejecucion);
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        fecha.setHours(0, 0, 0, 0);
        const vencido = fecha < hoy;
        return (
          <span className={vencido ? 'badge badge-danger' : ''}>
            {new Date(row.proxima_ejecucion).toLocaleDateString('es-CL')}
          </span>
        );
      }
    },
    {
      key: 'total_generados',
      label: 'Generados',
      render: (row) => (
        <span className="badge badge-info">{row.total_generados || 0}</span>
      )
    },
    { key: 'acciones', label: 'Acciones', align: 'center' }
  ], []);

  const today = getDateKey(new Date());

  const createFields = useMemo(() => [
    { name: 'nombre', label: 'Nombre del Programa', type: 'text', required: true, maxLength: 150 },
    {
      name: 'equipo_id',
      label: 'Equipo',
      type: 'select',
      required: true,
      options: equipos.map(e => ({ value: e.id, label: `${e.codigo} - ${e.marca} ${e.modelo}` }))
    },
    { name: 'descripcion', label: 'Descripcion', type: 'textarea', required: true },
    { name: 'prioridad', label: 'Prioridad', type: 'select', width: 'half', options: PRIORIDAD_OPTIONS },
    { name: 'frecuencia_tipo', label: 'Frecuencia', type: 'select', required: true, width: 'half', options: FRECUENCIA_TIPO_OPTIONS },
    { name: 'frecuencia_valor', label: 'Cada N periodos', type: 'number', required: true, min: 1, width: 'half' },
    {
      name: 'proxima_ejecucion',
      label: 'Primera Ejecución',
      type: 'date',
      required: true,
      width: 'half',
      min: today,
      validators: [(value) => validateDate(value, 'Próxima ejecución')]
    },
    {
      name: 'plantilla_mantenimiento_id',
      label: 'Plantilla',
      type: 'select',
      required: true,
      options: plantillas.filter(p => p.cantidad_actividades > 0).map(p => ({ value: p.id, label: p.nombre }))
    }
  ], [equipos, plantillas, today]);

  const editFields = useMemo(() => [
    { name: 'nombre', label: 'Nombre del Programa', type: 'text', required: true, maxLength: 150 },
    { name: 'descripcion', label: 'Descripcion', type: 'textarea', required: true },
    { name: 'prioridad', label: 'Prioridad', type: 'select', width: 'half', options: PRIORIDAD_OPTIONS },
    { name: 'frecuencia_tipo', label: 'Frecuencia', type: 'select', required: true, width: 'half', options: FRECUENCIA_TIPO_OPTIONS },
    { name: 'frecuencia_valor', label: 'Cada N periodos', type: 'number', required: true, min: 1, width: 'half' }
  ], []);

  const filterConfig = useMemo(() => [
    {
      name: 'search',
      type: 'text',
      placeholder: 'Buscar por nombre, equipo...',
      icon: 'fa-search'
    },
    {
      name: 'frecuencia_tipo',
      type: 'select',
      placeholder: 'Todas las frecuencias',
      options: [
        { value: 'diaria', label: 'Diaria' },
        { value: 'semanal', label: 'Semanal' },
        { value: 'mensual', label: 'Mensual' },
        { value: 'anual', label: 'Anual' }
      ]
    }
  ], []);

  const programaActions = [
    { icon: 'fa-wrench', className: 'btn-ghost-info',    title: 'Ver órdenes vinculadas', to: (row) => `${ROUTES.ORDENES_TRABAJO}?programa_mantenimiento_id=${row.id}`,                                                                                                                show: () => hasPermission('ordenes_trabajo.read_all') },
    { icon: 'fa-pause',  className: 'btn-ghost-warning', title: 'Pausar programa',        onClick: handlePause,  show: (row) => row.activo === 1 && hasPermission('programas.update'),                                                                                                 disabled: (row) => togglingId === row.id },
    { icon: 'fa-play',   className: 'btn-ghost-success', title: 'Reanudar programa',      onClick: handleResume, show: (row) => row.activo !== 1 && (hasPermission('programas.reactivate') || (row.has_active_maintenance && hasPermission('programas.update'))),                      disabled: (row) => togglingId === row.id },
    { icon: 'fa-edit',   className: 'btn-ghost-primary', title: 'Editar',                 onClick: handleEdit,   show: () => hasPermission('programas.update') },
    { icon: 'fa-trash',  className: 'btn-ghost-danger',  title: 'Eliminar',               onClick: handleDelete, show: () => hasPermission('programas.delete') },
  ];

  const programaMobileCardRenderer = useCallback((row, index, visibleActions) => {
    const fecha = new Date(row.proxima_ejecucion);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    fecha.setHours(0, 0, 0, 0);
    const vencido = fecha < hoy;
    return (
      <div key={row.id || index} className="mobile-card">
        <div className="mobile-card-header">
          <span className="mobile-card-title">
            <HighlightedText text={row.nombre || ''} searchTerm={filters.search} />
          </span>
          {row.activo === 0 && <span className="badge badge-warning">Pausado</span>}
        </div>
        <div className="mobile-card-body">
          <div className="mobile-card-field">
            <span className="mobile-card-label">Equipo</span>
            <span className="mobile-card-value">{renderEquipoCell(row.equipo, filters.search)}</span>
          </div>
          <div className="mobile-card-field">
            <span className="mobile-card-label">Plantilla</span>
            <span className="mobile-card-value">{row.plantilla?.nombre || <span className="text-muted">—</span>}</span>
          </div>
          <div className="mobile-card-field">
            <span className="mobile-card-label">Frecuencia</span>
            <span className="mobile-card-value">{formatFrecuencia(row)}</span>
          </div>
          <div className="mobile-card-field">
            <span className="mobile-card-label">Próxima</span>
            <span className="mobile-card-value">
              <span className={vencido ? 'badge badge-danger' : ''}>
                {new Date(row.proxima_ejecucion).toLocaleDateString('es-CL')}
              </span>
            </span>
          </div>
        </div>
        {visibleActions?.length > 0 && (
          <div className="card-actions">
            {visibleActions.map(action => renderActionButton(action, row, action.className?.includes('danger') ? 'btn-table-action' : 'btn-sm'))}
          </div>
        )}
      </div>
    );
  }, [filters.search]);

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Programas' }
        ]} />

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          filterConfig={filterConfig}
          actionConfig={[
            { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            { label: 'Nuevo Programa', icon: 'fa-plus', variant: 'primary', onClick: handleCreate, show: () => hasPermission('programas.create') }
          ]}
        />

        <EntityTable
          columns={columns}
          data={programas}
          loading={loading}
          searchTerm={filters.search}
          emptyMessage="No se encontraron programas"
          animateRows={true}
          actions={programaActions}
          mobileCardRenderer={programaMobileCardRenderer}
        />

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          title={editingPrograma ? 'Editar Programa' : 'Nuevo Programa'}
          fields={editingPrograma ? editFields : createFields}
          initialData={editingPrograma}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Programa"
          message="¿Esta seguro de eliminar este programa?"
          confirmText="Eliminar"
          variant="danger"
        />

        <EntityModal
          isOpen={reactivateModal.open}
          onClose={() => { setReactivateModal({ open: false, programa: null }); setTogglingId(null); }}
          onSave={handleReactivate}
          title="Reactivar Programa"
          fields={[{
            name: 'proxima_ejecucion',
            label: 'Fecha próxima mantención',
            type: 'date',
            required: true,
            min: today,
            validators: [(value) => validateDate(value, 'Próxima ejecución')]
          }]}
          initialData={null}
          loading={reactivating}
          submitLabel="Reactivar"
        />
      </div>
    </>
  );
}

export default Programas;
